import React from "react";
import { Box, Button, styled } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RemoveIcon from "@mui/icons-material/Remove";
import TuneIcon from "@mui/icons-material/Tune";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { colors, fontWeight } from "theme/defaultStyle";
import { GlobalSearchCriterion, GlobalSearchQueueRow } from "./globalSearchConfig";

const Root = styled(Box)({
  "& .gs-qhead-row": {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "11px 16px 9px",
    borderBottom: `1px solid ${colors.grey17}`,
  },
  "& .gs-qhead": {
    flex: 1,
    fontSize: 11,
    color: colors.grey22,
    lineHeight: 1.5,
    "& strong": { color: colors.grey08, fontWeight: fontWeight.bold1 },
  },
  "& .gs-qhead-change": {
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontWeight: fontWeight.bold1,
    color: colors.blue15,
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: "none",
    border: "none",
    padding: 0,
    "&:hover": { textDecoration: "underline" },
    "& .MuiSvgIcon-root": { fontSize: 15 },
  },
  "& .gs-qrow": {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: `1px solid ${colors.blue23}`,
    "&:last-child": { borderBottom: "none" },
    "&:hover, &.sel": { backgroundColor: colors.blue29 },
    "&.off": {
      cursor: "default",
      opacity: 0.48,
      "&:hover": { backgroundColor: "transparent" },
      "& .gs-qrow-count": { color: colors.grey15, backgroundColor: colors.grey31 },
    },
  },
  "& .gs-qrow-name": {
    fontSize: 11.5,
    fontWeight: fontWeight.bold2,
    color: colors.grey08,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  "& .gs-qrow-count": {
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
    backgroundColor: colors.blue23,
    borderRadius: 10,
    padding: "2px 10px",
    minWidth: 28,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },
  "& .gs-qrow-open": {
    marginLeft: "auto",
    fontSize: 11,
    color: colors.grey22,
    fontStyle: "italic",
  },
  "& .gs-qrow-go": { fontSize: 18, color: colors.grey15 },
  "& .gs-state": { padding: "22px 20px", textAlign: "center" },
  "& .gs-state-icon": {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: colors.blue29,
    color: colors.blue22,
    marginBottom: 9,
    "& .MuiSvgIcon-root": { fontSize: 19 },
  },
  "& .gs-state.gs-error .gs-state-icon": {
    backgroundColor: "#fbe4e4",
    color: colors.red01,
  },
  "& .gs-state-title": { fontSize: 13, fontWeight: fontWeight.bold1, color: colors.grey08 },
  "& .gs-state-sub": { fontSize: 11.5, color: colors.grey22, marginTop: 4, lineHeight: 1.5 },
  "& .gs-state-action": {
    marginTop: 13,
    height: 28,
    fontSize: 11.5,
    textTransform: "none",
    color: colors.blue15,
    borderColor: colors.blue15,
    "& .MuiSvgIcon-root": { fontSize: 16 },
  },
});

// "Agent ID 433333" / "A and B" / "A, B and C", each part in bold.
const CriteriaText = ({ criteria }: { criteria: GlobalSearchCriterion[] }) => (
  <>
    {criteria.map((c, i) => (
      <React.Fragment key={c.key}>
        {i > 0 && (i === criteria.length - 1 ? " and " : ", ")}
        <strong>{`${c.label} ${c.value}`}</strong>
      </React.Fragment>
    ))}
  </>
);

type StateBlockProps = {
  error?: boolean;
  icon: React.ReactNode;
  title: string;
  sub: React.ReactNode;
  actionLabel: string;
  actionIcon: React.ReactNode;
  onAction: () => void;
};

const StateBlock = ({ error, icon, title, sub, actionLabel, actionIcon, onAction }: StateBlockProps) => (
  <Box className={`gs-state${error ? " gs-error" : ""}`}>
    <span className="gs-state-icon">{icon}</span>
    <Box className="gs-state-title">{title}</Box>
    <Box className="gs-state-sub">{sub}</Box>
    <Button className="gs-state-action" variant="outlined" startIcon={actionIcon} onClick={onAction}>
      {actionLabel}
    </Button>
  </Box>
);

type Props = {
  criteria: GlobalSearchCriterion[];
  rows: GlobalSearchQueueRow[] | null;
  error: boolean;
  selected: number;
  onHover: (index: number) => void;
  onOpenQueue: (row: GlobalSearchQueueRow) => void;
  onChangeSearch: () => void;
  onRetry: () => void;
};

const GlobalSearchResults = ({
  criteria,
  rows,
  error,
  selected,
  onHover,
  onOpenQueue,
  onChangeSearch,
  onRetry,
}: Props) => {
  if (error || !rows) {
    return (
      <Root>
        <StateBlock
          error
          icon={<ErrorOutlineIcon />}
          title="Could not run the search"
          sub="Something went wrong. Your criteria were kept."
          actionLabel="Retry"
          actionIcon={<RefreshIcon />}
          onAction={onRetry}
        />
      </Root>
    );
  }

  const counted = rows.filter((r) => r.countable);
  const foundIn = counted.filter((r) => r.count > 0).length;
  const anyFound = rows.some((r) => r.enabled);
  const multi = criteria.length > 1;

  if (!anyFound) {
    const plain = criteria.map((c) => `${c.label} ${c.value}`).join(" + ");
    return (
      <Root>
        <StateBlock
          icon={<SearchOffIcon />}
          title="No records found"
          sub={`Nothing in the four queues matches ${plain}${multi ? ", on every field" : ""}.`}
          actionLabel="Change search"
          actionIcon={<TuneIcon />}
          onAction={onChangeSearch}
        />
      </Root>
    );
  }

  const plural = counted.length === 1 ? "" : "s";
  return (
    <Root>
      <Box className="gs-qhead-row">
        <Box className="gs-qhead">
          {foundIn
            ? `Found in ${foundIn} of ${counted.length} working queue${plural}`
            : `No match in the working queue${plural}`}{" "}
          for <CriteriaText criteria={criteria} />
          {multi ? " (all must match)" : ""}
        </Box>
        <button type="button" className="gs-qhead-change" onClick={onChangeSearch}>
          <TuneIcon />
          Change search
        </button>
      </Box>
      <Box role="listbox">
        {rows.map((r, i) => (
          <Box
            key={r.key}
            role="option"
            aria-selected={r.enabled && i === selected}
            aria-disabled={!r.enabled}
            className={`gs-qrow${r.enabled ? "" : " off"}${r.enabled && i === selected ? " sel" : ""}`}
            onClick={r.enabled ? () => onOpenQueue(r) : undefined}
            onMouseEnter={r.enabled ? () => onHover(i) : undefined}
          >
            <span className="gs-qrow-name">{r.label}</span>
            {r.countable ? (
              <span className="gs-qrow-count">{r.count}</span>
            ) : (
              <span className="gs-qrow-open">{r.enabled ? "records found" : "no records"}</span>
            )}
            {r.enabled ? (
              <ChevronRightIcon className="gs-qrow-go" />
            ) : (
              <RemoveIcon className="gs-qrow-go" />
            )}
          </Box>
        ))}
      </Box>
    </Root>
  );
};

export default GlobalSearchResults;
